import { AxiosInstance, AxiosProgressEvent } from "axios"

//import ApiService from "./api.service"
import AppService from "./app.service"
import { USER } from "@/config/api.endpoints"

export enum AppUserDetailsLinks {
  NONE = 0,
  TIDAL = 10,
  APPLE_MUSIC = 20,
}

export interface AppUserDetailsGenre {
  name: string
}

export interface AppUserDetailsSocials {
  instagram: string
  x: string
  tiktok: string
  twitch: string
  snap: string
  soundcloud: string
  facebook: string 
}

export interface AppUserDetailsStatistics {
  current: AppUserDetailsStatistic
  data?:  Map<string, AppUserDetailsStatistic>
  periodicity?: "Month" | "Week" | "Day"
}

export interface AppUserDetailsStatistic {
  possibleBalance: number
  fulfilledBalance: number
  reqstsReceived: number
  reqstsFulfilled: number
}

export interface AppUserDetails extends AppUserProfile {
  id: string
  username: string
  name: string
  userImageUrl: string
  city: string
  state: string
  country: string
  aboutMe: string
  genres: Array<AppUserDetailsGenre>
  socials: AppUserDetailsSocials
  email: string
  phoneNumber: string

  links: AppUserDetailsLinks[]
  theme: "light" | "dark"
  subscription: "pro"
  homeTown: string
  firstName:  string
  lastName: string
  balance: number
  availableBalance: number
  statistics: AppUserDetailsStatistics
}

export interface AppUserProfile {
  currency: string
  rate: number
}

export const createUserService = (client: AxiosInstance) => {
  return {
    getLocalUsername: () => localStorage.getItem("username") || undefined,

    setLocalUsername: (username: string) => localStorage.setItem("username", username),

    clearLocalUsername: () => localStorage.removeItem("username"),

    isUsernameAvailable: async (username: string) => {
      try {
          const { data } = await client.get(`${USER.CHECK}?username=${encodeURIComponent(username)}`)
        return data
      } catch(error) {
        console.error(error)
        throw error
      }
    },

    createUser: async (formData: FormData, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) => {
      try {
        const { data } = await client.post(USER.REGISTER,
          formData,
          {
            onUploadProgress,
            headers: { "Content-Type": "multipart/form-data" },
          }
        )
        return data
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    getCurrentUser: async (): Promise<AppUserDetails> => {
      try {
        const { data } = await client.get<AppUserDetails>(USER.ME)
        return data
      } catch (error) {
        console.error("Failed to get current user status:", error)
        throw error
      }
    },

    getProfile: async (username: string) => {
      try {
        const { data } = await client.get<AppUserProfile>(`${USER.PROFILE}/${username}`)
        return data
      } catch (error) {
        console.error("Failed to get current user status:", error)
        throw error
      }
    },

    resetUser: async (userId: string) => {
      try {
        const { data } = await client.post(USER.RESET, { userId })
        return data
      } catch (error) {
        console.error("Failed to get current user status:", error)
        throw error
      }
    },

    resendConfirmationLink: async () => {
      try {
        const { data } = await client.post(USER.RESEND_CONFIRMATION)
        return data
      } catch (error) {
        console.error("Failed to get current user status:", error)
        throw error
      }
    },

    confirmUser: async (token: string, nextStep: string): Promise<void> => {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'token'
      tokenInput.value = token

      const appBaseUrl = document.createElement('input')
      appBaseUrl.type = 'hidden'
      appBaseUrl.name = 'appBaseUrl'
      appBaseUrl.value = AppService.getAppBaseURL()

      const successPath = document.createElement('input')
      successPath.type = 'hidden'
      successPath.name = 'successPath'
      successPath.value = nextStep

      const form = document.createElement('form')

      form.method = 'POST'
      form.action = `${AppService.getApiBaseURL()}${USER.CONFIRMATION}`
      form.appendChild(tokenInput)
      form.appendChild(appBaseUrl)
      form.appendChild(successPath)

      document.body.appendChild(form)

      form.submit()
    },
  }
}

export type UserService = ReturnType<typeof createUserService>;
